import { create, StateCreator, StoreApi, UseBoundStore } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createShallowStore } from "./create-shallow-store";

/**
 * Creates a Zustand store with optional persistence and shallow selector generation.
 *
 * @template T - The type of the store state
 * @param stateCreator - A Zustand StateCreator function
 * @param options - Configuration options
 * @param options.persist - Storage type for persistence ('localStorage' or 'sessionStorage')
 * @param options.persistName - Storage key name (required when persist is set)
 * @param options.version - Schema version number for migrations (optional, defaults to 0)
 * @param options.migrate - Migration function to transform old data to new schema
 * @param options.createShallow - Whether to create a shallow store hook (defaults to true)
 * @returns An object containing useStore and optionally useShallowStore
 *
 * @example
 * // Basic store with both useStore and useShallowStore
 * const { useStore, useShallowStore } = createStore<MyState>((set) => ({
 *   count: 0,
 *   increment: () => set((state) => ({ count: state.count + 1 })),
 * }));
 *
 * @example
 * // Store with localStorage persistence
 * const { useStore, useShallowStore } = createStore<MyState>(
 *   (set) => ({ count: 0 }),
 *   { persist: 'localStorage', persistName: 'my-store' }
 * );
 *
 * @example
 * // Store with versioning and migration
 * const { useStore, useShallowStore } = createStore<MyState>(
 *   (set) => ({ count: 0, newField: 'default' }),
 *   {
 *     persist: 'localStorage',
 *     persistName: 'my-store',
 *     version: 2,
 *     migrate: (persistedState, version) => {
 *       if (version === 1) {
 *         // Transform v1 data to v2 format
 *         return { ...persistedState, newField: 'default' };
 *       }
 *       return persistedState;
 *     }
 *   }
 * );
 *
 * @example
 * // Store without shallow selector
 * const { useStore } = createStore<MyState>(
 *   (set) => ({ count: 0 }),
 *   { createShallow: false }
 * );
 */

// Overload: When createShallow is explicitly false
export function createStore<T>(
  stateCreator: StateCreator<T>,
  options: {
    persist?: "localStorage" | "sessionStorage";
    name: string;
    version?: number;
    migrate?: (persistedState: unknown, version: number) => T | Promise<T>;
    createShallow: false;
  }
): { useStore: UseBoundStore<StoreApi<T>> };

// Overload: When createShallow is true or undefined (default)
// eslint-disable-next-line no-redeclare
export function createStore<T>(
  stateCreator: StateCreator<T>,
  options?: {
    persist?: "localStorage" | "sessionStorage";
    name: string;
    version?: number;
    migrate?: (persistedState: unknown, version: number) => T | Promise<T>;
    createShallow?: true;
  }
): { useStore: UseBoundStore<StoreApi<T>>; useShallowStore: ReturnType<typeof createShallowStore<T>> };

// Implementation signature
// eslint-disable-next-line no-redeclare
export function createStore<T>(
  stateCreator: StateCreator<T>,
  options?: {
    persist?: "localStorage" | "sessionStorage";
    name: string;
    version?: number;
    migrate?: (persistedState: unknown, version: number) => T | Promise<T>;
    createShallow?: boolean;
  }
): { useStore: UseBoundStore<StoreApi<T>>; useShallowStore?: ReturnType<typeof createShallowStore<T>> } {
  const { name: persistName, version = 0, migrate, createShallow = true as boolean } = options || {};
  const persistType = options !== undefined && "persist" in options ? options.persist : undefined;

  // Runtime validation: if persist is set, persistName must be provided
  if (persistType && !persistName) {
    throw new Error("persistName is required when persist option is set");
  }

  // Storage mapping for persist middleware
  const storageMap = {
    localStorage: typeof window !== "undefined" ? window.localStorage : undefined,
    sessionStorage: typeof window !== "undefined" ? window.sessionStorage : undefined,
  };

  // Create the base store with or without persistence
  let useStore: UseBoundStore<StoreApi<T>>;

  if (persistType && persistName) {
    const storage = storageMap[persistType];
    if (!storage) {
      throw new Error(`Storage type "${persistType}" is not available in this environment`);
    }

    useStore = create<T>()(
      persist(stateCreator, {
        name: persistName,
        storage: createJSONStorage(() => storage),
        version: version,
        migrate: migrate,
      })
    );
  } else {
    useStore = create<T>()(stateCreator);
  }

  // Create shallow store if requested
  if (createShallow !== false) {
    const useShallowStore = createShallowStore(useStore);
    return { useStore, useShallowStore };
  }

  return { useStore };
}
